import {
  computed,
  h,
  ref,
  toValue,
  useId,
  type Component,
  type MaybeRefOrGetter,
} from '#imports';
import {
  parseRangeSelector,
  type ShortcodeRenderer,
} from '@nuxt-shared/lib/cms-shortcodes';
import type {
  AccordionComponentItem,
  AccordionItem,
  AccordionProperty,
  PublicationAccordionProps,
} from './types';

/**
 * Свойство `C_ACCORDION` → элементы аккордеона. Заголовки лежат в `VALUE`,
 * описания — в одноимённых позициях `DESCRIPTION`. Элементы без заголовка
 * пропускаем, но нумерацию не сдвигаем: шорткод адресует позицию в свойстве.
 */
export function parseAccordionItems(
  source?: AccordionProperty | AccordionComponentItem[] | null,
): AccordionItem[] {
  const titles = Array.isArray(source)
    ? source.map((item) => String(item?.VALUE ?? ''))
    : toArray(source?.VALUE);
  const texts = Array.isArray(source)
    ? source.map((item) => String(item?.DESCRIPTION ?? ''))
    : toArray(source?.DESCRIPTION);

  return titles.reduce<AccordionItem[]>((acc, title, position) => {
    if (title.trim()) {
      acc.push({ index: position + 1, title, text: texts[position] ?? '' });
    }
    return acc;
  }, []);
}

/**
 * Отбирает элементы по параметрам шорткода: `#accordion#` — все,
 * `#accordion_2-5#` — диапазон, `#accordion_3,5#` — перечисление.
 */
export function pickAccordionItems(
  items: AccordionItem[],
  params?: string,
): AccordionItem[] {
  const selected = parseRangeSelector(params);
  if (!selected) return items;

  return items.filter((item) => selected.has(item.index));
}

/**
 * Рендер шорткода `#accordion…#` для `CmsContent`. Шорткод, под который не
 * нашлось ни одного элемента, исчезает из текста — пустой массив в ответе.
 */
export function createAccordionShortcode(
  items: MaybeRefOrGetter<AccordionItem[]>,
  AccordionComponent: Component,
  title: MaybeRefOrGetter<string> = '',
): ShortcodeRenderer {
  return (params) => {
    const picked = pickAccordionItems(toValue(items), params);
    return picked.length
      ? h(AccordionComponent, { items: picked, title: toValue(title) })
      : [];
  };
}

export function usePublicationAccordionModel(props: PublicationAccordionProps) {
  // Все пункты свёрнуты на первом рендере — SSR и клиент дают одинаковый DOM.
  // Set мутируем через add/delete, поэтому `ref`, а не `shallowRef`.
  const opened = ref(new Set<number>());
  // На странице может быть несколько блоков (`#accordion_1#` и `#accordion_2-5#`) —
  // без своего префикса их `aria-controls` указывали бы на одни и те же панели.
  const uid = useId();

  const isOpen = (index: number): boolean => opened.value.has(index);

  const panelId = (index: number): string => `${uid}-accordion-${index}`;

  const toggle = (index: number) => {
    if (opened.value.has(index)) opened.value.delete(index);
    else opened.value.add(index);
  };

  return {
    items: computed(() => props.items),
    title: computed(() => props.title?.trim() ?? ''),
    isOpen,
    panelId,
    toggle,
  };
}

/** Битрикс отдаёт множественное свойство массивом, одиночное — строкой. */
function toArray(value?: string | string[] | null): string[] {
  if (Array.isArray(value)) return value.map((item) => String(item ?? ''));
  return value ? [String(value)] : [];
}
